import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Upload, Download, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';

const PRODUCT_CATEGORY: CategoryKey = 'product';

interface ImportError { row: number; field: string; message: string; }
interface ImportPreviewData { [key: string]: any; }
interface ImportFailure { row: number; rowData: ImportPreviewData; error: string; }

const ProductStockImport = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [importFailures, setImportFailures] = useState<ImportFailure[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState<'idle' | 'preview' | 'success' | 'error'>('idle');
  const [progress, setProgress] = useState<{done: number; total: number}>({ done: 0, total: 0 });
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
      } catch {}
    })();
  }, []);

  const allColumns = [
    'store_id',
    'sku_id',
    'hktvmall_stock_status',
    'merchant_inventory_qty',
    'product_ready_method',
    'hktvmall_visible_invisible',
    'merchant_name',
  ];

  const toNullable = (v: any) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim()) : v);
  const toNumberOrNull = (v: any) => {
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '' || t === null || t === undefined) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // Security: Sanitize text to prevent XSS and Excel Formula Injection
  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    let sanitized = text.trim();

    // HTML escape to prevent XSS while preserving content (e.g., "<此日期前最佳：2026.12>" becomes "&lt;此日期前最佳：2026.12&gt;")
    // Escape order matters: & must be escaped first to avoid double-escaping
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Prevent Excel Formula Injection by prefixing dangerous formulas with '
    if (/^[=+\-@]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }

    // Additional security: Limit text length to prevent DoS
    if (sanitized.length > 5000) {
      sanitized = sanitized.substring(0, 5000) + '...';
    }

    return sanitized;
  };

  // Security: Validate numeric fields with range checks
  const validateNumericField = (value: any, fieldName: string, min: number = 0): { isValid: boolean; error?: string } => {
    const num = toNumberOrNull(value);
    if (num === null) return { isValid: true }; // Allow null values

    if (!Number.isFinite(num)) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}必須是有效的數字` : `${fieldName} must be a valid number` };
    }

    if (num < min) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}不能小於 ${min}` : `${fieldName} cannot be less than ${min}` };
    }

    // Additional check for extremely large numbers (potential DoS)
    if (num > 999999999) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}數值過大，請檢查數據` : `${fieldName} value too large, please check data` };
    }

    return { isValid: true };
  };

  const mapRowToPayload = (row: ImportPreviewData): Record<string, any> => ({
    store_id: toNullable(row.store_id),
    sku_id: toNullable(row.sku_id),
    hktvmall_stock_status: sanitizeText(row.hktvmall_stock_status),
    merchant_inventory_qty: toNumberOrNull(row.merchant_inventory_qty),
    product_ready_method: sanitizeText(row.product_ready_method),
    hktvmall_visible_invisible: sanitizeText(row.hktvmall_visible_invisible),
    merchant_name: sanitizeText(row.merchant_name),
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Security: File size limit to prevent DoS attacks
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
      if (file.size > MAX_SIZE) {
        toast.error(language === 'zh' ? '檔案太大！請上傳 5MB 以下的檔案。' : 'File too large! Please upload files under 5MB.');
        if (event.target) {
          (event.target as HTMLInputElement).value = '';
        }
        return;
      }

      // Require current company code to be present before parsing, so we can validate Store ID
      const companyCode = (currentCompany?.code || '').toString().trim();
      if (!companyCode) {
        toast.error(language === 'zh' ? '未設定當前公司的「公司代碼」，無法驗證 Store ID。' : 'Company code is not set. Cannot validate Store ID.');
        if (event.target) {
          (event.target as HTMLInputElement).value = '';
        }
        return;
      }
      setSelectedFile(file);
      parseFile(file);
    }
  };

  const parseFile = (file: File) => {
    setIsProcessing(true);
    setImportStatus('preview');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsedData: ImportPreviewData[] = [];
        const validationErrors: ImportError[] = [];
        const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
        let headers: string[] = [];
        let dataRows: string[][] = [];
        if (isExcel) {
          const wb = XLSX.read(new Uint8Array(e.target?.result as ArrayBuffer), { type: 'array' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json<any>(sheet, { header: 1, defval: '' }) as any[];
          if (rows.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = (rows[0] as any[]).map((v) => String(v));
          dataRows = rows.slice(1) as any[];
        } else {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((r) => r.trim());
          if (lines.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
          dataRows = lines.slice(1).map((line) => line.split(',').map((v) => v.trim().replace(/"/g, '')));
        }
        const normalized = (s: string) => s?.toString().trim().toLowerCase();
        const headerMap: Record<string, string> = {
          'store id': 'store_id',
          'store_id': 'store_id',
          'Store ID': 'store_id',
          '商店代碼': 'store_id',
          'sku id': 'sku_id',
          'SKU ID': 'sku_id',
          '產品 id': 'sku_id',
          '產品id': 'sku_id',
          '產品ID': 'sku_id',
          'hktvmall stock status': 'hktvmall_stock_status',
          'hktvmall_stock_status': 'hktvmall_stock_status',
          'hktvmall 庫存狀態': 'hktvmall_stock_status',
          '庫存狀態': 'hktvmall_stock_status',
          'merchant inventory qty': 'merchant_inventory_qty',
          'merchant_inventory_qty': 'merchant_inventory_qty',
          'qty on hand': 'merchant_inventory_qty',
          'qty_on_hand': 'merchant_inventory_qty',
          '現存量': 'merchant_inventory_qty',
          'product ready method': 'product_ready_method',
          'product_ready_method': 'product_ready_method',
          'hktvmall visible/invisible': 'hktvmall_visible_invisible',
          'hktvmall visible / invisible': 'hktvmall_visible_invisible',
          'visible/invisible': 'hktvmall_visible_invisible',
          'hktvmall_visible_invisible': 'hktvmall_visible_invisible',
          'merchant name': 'merchant_name',
          'merchant_name': 'merchant_name',
        };
        const mappedKeys: (string | null)[] = headers.map((h) => {
          const key = headerMap[normalized(h)];
          if (!key && allColumns.includes(h)) return h;
          return key || null;
        });
        // 檢查必要欄位是否存在（避免使用錯誤模板時出現空白預覽而誤以為當機）
        const requiredColumns: string[] = ['store_id', 'sku_id'];
        const hasAllRequired = requiredColumns.every((col) => mappedKeys.includes(col));
        if (!hasAllRequired) {
          const msg = language === 'zh' ? '找不到必要欄位（需包含：store_id、sku_id）' : 'Missing required columns (need: store_id, sku_id)';
          throw new Error(msg);
        }
        const expectedCode = (currentCompany?.code || '').toString().trim();
        if (!expectedCode) {
          throw new Error(language === 'zh' ? '未設定公司代碼，無法核對 Store ID' : 'Company code not set; cannot validate Store ID');
        }
        dataRows.forEach((cols, rowIdx) => {
          if (!cols || cols.length === 0) return;
          const obj: ImportPreviewData = {};
          mappedKeys.forEach((dbKey, ci) => {
            if (!dbKey) return;
            obj[dbKey] = cols[ci] ?? '';
          });
          if (!obj.sku_id) validationErrors.push({ row: rowIdx + 3, field: 'sku_id', message: language === 'zh' ? '缺少 SKU ID' : 'Missing SKU ID' });

          // Security: Enhanced numeric validation with range checks
          const inventoryValidation = validateNumericField(obj.merchant_inventory_qty, language === 'zh' ? '現存量' : 'Inventory QTY');
          if (!inventoryValidation.isValid) {
            validationErrors.push({ row: rowIdx + 3, field: 'merchant_inventory_qty', message: inventoryValidation.error! });
          }

          // 驗證 Store ID 與目前公司代碼一致
          const storeIdVal = (obj.store_id ?? '').toString().trim();
          if (expectedCode) {
            if (!storeIdVal) {
              validationErrors.push({ row: rowIdx + 3, field: 'store_id', message: language === 'zh' ? '缺少 Store ID' : 'Missing Store ID' });
            } else if (storeIdVal.toUpperCase() !== expectedCode.toUpperCase()) {
              const msg = language === 'zh'
                ? `Store ID (${storeIdVal}) 與公司代碼 (${expectedCode}) 不一致`
                : `Store ID (${storeIdVal}) does not match company code (${expectedCode})`;
              validationErrors.push({ row: rowIdx + 3, field: 'store_id', message: msg });
            }
          }
          parsedData.push(obj);
        });
        setPreviewData(parsedData);
        setErrors(validationErrors);
        if (validationErrors.length > 0) {
          toast.error(
            language === 'zh'
              ? `檔案驗證失敗，共 ${validationErrors.length} 筆錯誤。請查看下方錯誤詳情並修正後再匯入。`
              : `File validation failed with ${validationErrors.length} errors. Please check error details below and fix before importing.`
          );
        } else {
          toast.success(language === 'zh' ? 'Excel 已準備，可隨時按下「確定滙入」' : 'Excel is ready. You can click "Confirm Import" anytime');
        }
      } catch (error: any) {
        toast.error('檔案解析失敗: ' + error.message);
        setImportStatus('error');
      } finally {
        setIsProcessing(false);
      }
    };
    if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  const downloadTemplate = () => {
    // 暂时禁用下载模板功能，之后会重新处理
    // TODO: 重新实现模板下载功能
  };

  const handleImport = async () => {
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '未能取得公司資訊，請稍後重試' : 'Company not ready');
      return;
    }
    const expectedCode = (currentCompany?.code || '').toString().trim();
    if (!expectedCode) {
      toast.error(language === 'zh' ? '未設定當前公司的「公司代碼」，無法匯入' : 'Company code not set; import is not allowed');
      return;
    }
    if (previewData.length === 0) {
      toast.error(language === 'zh' ? '請先選擇檔案' : 'Please select a file first');
      return;
    }
    if (errors.length > 0) {
      toast.error(language === 'zh' ? '檔案仍有錯誤，請修正後再匯入' : 'There are validation errors. Please fix them before importing');
      return;
    }
    // 嚴格檢查所有列的 Store ID 是否與公司代碼完全一致（不分大小寫）
    const mismatch = previewData.some((row) => String(row.store_id ?? '').trim().toUpperCase() !== expectedCode.toUpperCase());
    if (mismatch) {
      toast.error(language === 'zh' ? '偵測到與公司代碼不一致的 Store ID，已阻止匯入' : 'Detected Store ID that does not match company code. Import blocked');
      return;
    }

    try {
      setIsProcessing(true);
      setImportFailures([]); // Clear previous failures
      setProgress({ done: 0, total: previewData.length });

      // 使用現有的封裝客戶端
      const { supabase } = await import('@/integrations/supabase/client');
      const companyIdNum = Number(currentCompany.id);

      // Security: Secure Batch Import - Process in chunks of 50 for better performance and error handling
      const BATCH_SIZE = 50;
      let success = 0;
      let failCount = 0;
      const failures: ImportFailure[] = [];

      // Step 1: Validate and prepare data for batch processing
      const validRows: Array<{ rowIndex: number; payload: Record<string, any> }> = [];
      for (let i = 0; i < previewData.length; i++) {
        const raw = previewData[i];
        const payload = mapRowToPayload(raw);

        const upsertBody: Record<string, any> = {
          company_id: companyIdNum,
          sku_id: payload.sku_id,
          store_id: payload.store_id,
          hktvmall_stock_status: payload.hktvmall_stock_status,
          merchant_inventory_qty: payload.merchant_inventory_qty,
          product_ready_method: payload.product_ready_method,
          hktvmall_visible_invisible: payload.hktvmall_visible_invisible,
          merchant_name: payload.merchant_name,
        };

        validRows.push({ rowIndex: i, payload: upsertBody });
      }

      // Step 2: Process in batches of 50
      const totalBatches = Math.ceil(validRows.length / BATCH_SIZE);
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * BATCH_SIZE;
        const endIdx = Math.min(startIdx + BATCH_SIZE, validRows.length);
        const batchRows = validRows.slice(startIdx, endIdx);
        const batchPayloads = batchRows.map(row => row.payload);

        try {
          // Batch upsert with adaptive column handling
          let attempts = 0;
          let currentPayloads = [...batchPayloads];

          while (attempts < 8 && currentPayloads.length > 0) {
            const { error: upErr } = await supabase
              .from('product_inventory')
              .upsert(currentPayloads, { onConflict: 'company_id,sku_id', ignoreDuplicates: false, defaultToNull: true });

            if (!upErr) break;

            const msg = String(upErr.message || '');
            const match = msg.match(/'(.*?)'\s+column/);
            const missing = match?.[1];

            if (missing) {
              // Remove problematic column from all payloads in this batch
              currentPayloads = currentPayloads.map(payload => {
                if (Object.prototype.hasOwnProperty.call(payload, missing)) {
                  delete (payload as any)[missing];
                }
                return payload;
              });
              attempts++;
              continue;
            }

            throw upErr;
          }

          // Verify batch write results
          const verificationPromises = batchRows.map(async (rowData) => {
            const skuId = rowData.payload.sku_id;
            const ok = skuId ? await supabase
              .from('product_inventory')
              .select('sku_id')
              .eq('company_id', companyIdNum)
              .eq('sku_id', skuId)
              .limit(1)
              .then(({ data, error }) => !error && Array.isArray(data) && data.length > 0) : false;
            return { rowData, verified: ok };
          });

          const verificationResults = await Promise.all(verificationPromises);

          verificationResults.forEach(({ rowData, verified }) => {
            if (verified) {
              success++;
            } else {
              failCount++;
              failures.push({
                row: rowData.rowIndex + 1,
                rowData: previewData[rowData.rowIndex],
                error: language === 'zh' ? '寫入驗證失敗：資料未正確儲存' : 'Write verification failed: data not saved correctly'
              });
            }
          });

        } catch (err: any) {
          // Batch failed - mark all rows in this batch as failed
          failCount += batchRows.length;
          batchRows.forEach(rowData => {
            failures.push({
              row: rowData.rowIndex + 1,
              rowData: previewData[rowData.rowIndex],
              error: language === 'zh' ? `資料庫錯誤: ${err.message || '未知錯誤'}` : `Database error: ${err.message || 'Unknown error'}`
            });
          });

          // Soft report every batch to avoid spam
          if ((batchIndex + 1) % Math.max(1, Math.floor(totalBatches / 10)) === 0) {
            console.warn('Batch stock import warning:', err?.message || err);
          }
        }

        // Update progress after each batch
        const processedRows = Math.min((batchIndex + 1) * BATCH_SIZE, validRows.length);
        setProgress({ done: processedRows + (previewData.length - validRows.length), total: previewData.length });
      }
      setImportFailures(failures);
      if (failCount === 0) {
        toast.success(language === 'zh' ? '匯入完成' : 'Import complete');
        setImportStatus('success');
      } else {
        toast.error(
          language === 'zh' ? `匯入失敗：${failCount} 筆資料未成功寫入。請查看下方失敗詳情。` : `Import failed: ${failCount} rows failed to write. Please check failure details below.`
        );
        setImportStatus('error');
      }
    } catch (e: any) {
      console.error(e);
      toast.error(language === 'zh' ? '匯入失敗' : 'Import failed');
      setImportStatus('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportFailures([]);
    setImportStatus('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '滙入功能（產品存量 - HKTVmall）' : 'Import Stock On-hand (HKTVmall)'}</h1>
          </div>
        </div>

        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '檔案上傳' : 'File Upload'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-foreground font-medium">{language === 'zh' ? '下載匯入模板' : 'Download Template'}</p>
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '使用HKTVmall 系統內的「庫存管理」匯出的產品 Excel 檔' : 'Use product Excel file exported from HKTVmall system\'s "Inventory Management"'}</p>
                  </div>
                </div>
                <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '下載模板' : 'Download'}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" id="stock-import-file" title={language === 'zh' ? '選擇匯入檔案（CSV 或 Excel）' : 'Choose import file (CSV or Excel)'} />
                  <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isProcessing}>
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'zh' ? '選擇檔案' : 'Choose File'}
                  </Button>
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-foreground">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-muted-foreground border-border">{(selectedFile.size / 1024).toFixed(1)} KB</Badge>
                      <Button variant="ghost" size="sm" onClick={resetImport} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
                {isProcessing && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                    <span>{language === 'zh' ? '處理中...' : 'Processing...'} {progress.done} / {progress.total}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {previewData.length > 0 && (
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">{language === 'zh' ? '資料預覽 (前 10 筆)' : 'Preview (first 10 rows)'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '商店代碼' : 'Store ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品 ID' : 'SKU ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? 'HKTVmall 庫存狀態' : 'HKTVmall Stock Status'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '現存量' : 'Merchant Inventory QTY'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, index) => (
                      <TableRow key={index} className="border-border hover:bg-accent/50">
                        <TableCell className="text-foreground text-sm">{String(row.store_id || '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row.sku_id || '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row.hktvmall_stock_status || '')}</TableCell>
                        <TableCell className="text-foreground text-sm">{String(row.merchant_inventory_qty ?? '')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4">
                <Button onClick={handleImport} disabled={errors.length > 0} className="bg-green-600 hover:bg-green-700 text-white">
                  {language === 'zh' ? '確認匯入' : 'Confirm Import'}
                </Button>
                {errors.length > 0 && (
                  <p className="text-red-400 text-sm mt-2">
                    {language === 'zh' ? '請先修正驗證錯誤再匯入' : 'Please fix validation errors before importing'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {errors.length > 0 && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {language === 'zh' ? '驗證錯誤詳情' : 'Validation Error Details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-red-400">
                  {language === 'zh' ? `發現 ${errors.length} 個驗證錯誤：` : `Found ${errors.length} validation errors:`}
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-red-600">
                        <TableHead className="text-red-400">{language === 'zh' ? '行號' : 'Row'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '欄位' : 'Field'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '錯誤訊息' : 'Error Message'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '行內容' : 'Row Content'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {errors.map((error, index) => {
                        const rowData = previewData[error.row - 3]; // Adjust for header rows
                        return (
                          <TableRow key={index} className="border-red-600">
                            <TableCell className="text-red-400 text-sm">{error.row}</TableCell>
                            <TableCell className="text-red-400 text-sm">{error.field}</TableCell>
                            <TableCell className="text-red-400 text-sm">{error.message}</TableCell>
                            <TableCell className="text-red-400 text-sm max-w-xs truncate">
                              {rowData ? JSON.stringify(rowData) : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {importFailures.length > 0 && (
          <Card className="bg-card/80 border-border mb-6">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {language === 'zh' ? '匯入失敗詳情' : 'Import Failure Details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-red-400">
                  {language === 'zh' ? `匯入過程中 ${importFailures.length} 筆資料失敗：` : `Import failed for ${importFailures.length} rows:`}
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-red-600">
                        <TableHead className="text-red-400">{language === 'zh' ? '行號' : 'Row'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '錯誤原因' : 'Error Reason'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '商店代碼' : 'Store ID'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '產品 ID' : 'SKU ID'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '庫存狀態' : 'Stock Status'}</TableHead>
                        <TableHead className="text-red-400">{language === 'zh' ? '現存量' : 'Inventory QTY'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importFailures.map((failure, index) => (
                        <TableRow key={index} className="border-red-600">
                          <TableCell className="text-red-400 text-sm">{failure.row}</TableCell>
                          <TableCell className="text-red-400 text-sm max-w-sm">{failure.error}</TableCell>
                          <TableCell className="text-red-400 text-sm">{String(failure.rowData.store_id || '')}</TableCell>
                          <TableCell className="text-red-400 text-sm">{String(failure.rowData.sku_id || '')}</TableCell>
                          <TableCell className="text-red-400 text-sm">{String(failure.rowData.hktvmall_stock_status || '')}</TableCell>
                          <TableCell className="text-red-400 text-sm">{String(failure.rowData.merchant_inventory_qty ?? '')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProductStockImport;


